import CalibrationReview from "./CalibrationReview";
import { teamOverview, overviewMetrics } from "../helpers/calibrationOverview";
import { summarizeDraft } from "../helpers/leagueAnalysisBatch";
import LeagueAnalysisBatch from "./LeagueAnalysisBatch";
import TeamContext from "./TeamContext";
import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "@mui/material";
import { useTranslation } from "react-i18next";
import { compareLeagueSelection } from "../helpers/leagueSelection";
import LeagueChoice from "./LeagueChoice";
import { leagueAnalysisRequest } from "../helpers/leagueAnalysisRequest";
import { tipsRequest } from "../helpers/tipsWorkflow";
import {
  detailFields,
  leagueFields,
  previewDetailDraft,
  detailSummary,
} from "../helpers/leagueAnalysisDraft";
import "./LeagueEvolution.css";
import "./LeagueAnalysis.css";
const newRunId = () =>
  window.crypto?.randomUUID?.() ||
  `analysis-${Date.now()}-${Math.random().toString(36).slice(2)}`;
export default function LeagueAnalysisEntry({
  leagues = [],
  onUpcoming,
  initialLeagueId,
  singleLeague = false,
  onSaved,
  onDirtyChange,
}) {
  const { t: tr, i18n } = useTranslation(),
    t = (key, args) => tr(`help.leagueSettingsAnalysis.${key}`, args);
  const [batchAdmin, setBatchAdmin] = useState(false),
    [batch, setBatch] = useState(null),
    [batchBusy, setBatchBusy] = useState(false),
    [batchOrigin, setBatchOrigin] = useState(null);
  const [catalog, setCatalog] = useState([]),
    [selected, setSelected] = useState(""),
    [context, setContext] = useState(null);
  const [profile, setProfile] = useState(50),
    [market, setMarket] = useState("normal"),
    [result, setResult] = useState(null);
  const [targets, setTargets] = useState(null),
    [preview, setPreview] = useState(null),
    [detail, setDetail] = useState(null),
    [editing, setEditing] = useState(false);
  const [phase, setPhase] = useState("catalog"),
    [error, setError] = useState(""),
    [saved, setSaved] = useState(false),
    [revisions, setRevisions] = useState([]);
  const [previewDirty, setPreviewDirty] = useState(false);
  const [runId, setRunId] = useState(() => newRunId()),
    sequence = useRef(0),
    mounted = useRef(true);
  const busy = !!phase || batchBusy,
    dirty = !!result && !saved;
  const invalidTargets = Object.values(targets || {}).some((row) =>
    Object.values(row).some(
      (v) => typeof v !== "number" || !Number.isFinite(v) || v <= 0
    )
  );
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  const fmt = (v) =>
    typeof v === "number" && Number.isFinite(v)
      ? new Intl.NumberFormat(i18n.language, {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        }).format(v)
      : "—";
  const percent = (v) =>
    typeof v === "number" && Number.isFinite(v)
      ? new Intl.NumberFormat(i18n.language, {
          style: "percent",
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }).format(v)
      : "—";
  const catalogIds = new Set(catalog.map((l) => Number(l.league_id)));
  const availableLeagues = leagues
    .filter((l) => catalogIds.has(Number(l.id)))
    .sort(compareLeagueSelection);
  const onProgress = (p) => setPhase(p.phase || "analyze");
  const request = (mode, extra = {}) =>
    leagueAnalysisRequest(
      {
        mode,
        league_id: context?.league.id,
        season_id: context?.season.id,
        revision: context?.revision,
        profile,
        market_orientation: market,
        language: i18n.language,
        ...extra,
      },
      onProgress
    );
  useEffect(() => {
    let active = true;
    mounted.current = true;
    tipsRequest("league-analysis", { mode: "catalog" })
      .then((r) => {
        if (active) {
          setCatalog(r.leagues);
          setBatchAdmin(r.batch_admin === true);
        }
      })
      .catch((e) => active && setError(e.message))
      .finally(() => active && setPhase(""));
    return () => {
      active = false;
      mounted.current = false;
    };
  }, []);
  const initialLoaded = useRef(false);
  useEffect(() => {
    if (initialLeagueId && catalog.length && !initialLoaded.current) {
      initialLoaded.current = true;
      if (catalog.some((l) => Number(l.league_id) === Number(initialLeagueId)))
        load(String(initialLeagueId));
    }
  }, [catalog, initialLeagueId]);
  useEffect(() => {
    if (!dirty) return;
    const guard = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    const navigateGuard = (e) => {
      if (!window.confirm(t("discardConfirm"))) e.preventDefault();
    };
    window.addEventListener("beforeunload", guard);
    window.addEventListener("betty:before-navigation", navigateGuard);
    return () => {
      window.removeEventListener("beforeunload", guard);
      window.removeEventListener("betty:before-navigation", navigateGuard);
    };
  }, [dirty]);
  function reset() {
    setBatchOrigin(null);
    setPreviewDirty(false);
    setResult(null);
    setTargets(null);
    setPreview(null);
    setDetail(null);
    setEditing(false);
    setSaved(false);
    setRunId(newRunId());
  }
  async function act(name, fn) {
    setPhase(name);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setPhase("");
    }
  }
  async function load(id) {
    const seq = ++sequence.current;
    reset();
    setContext(null);
    setRevisions([]);
    setSelected(id);
    if (!id) return;
    await act("loading", async () => {
      const league = catalog.find((l) => String(l.league_id) === String(id)),
        season = league.seasons[0];
      const r = await request("context", {
        league_id: Number(id),
        season_id: season.id,
      });
      if (seq !== sequence.current || !mounted.current) return;
      setContext(r.context);
      if (r.context.atomic_save_available) {
        const rev = await request("revisions", {
          league_id: Number(id),
          season_id: season.id,
        });
        if (seq === sequence.current) setRevisions(rev.revisions);
      }
    });
  }
  const en = i18n.language.startsWith("en");
  const sourceHint = (key) =>
    ({
      actual: en
        ? "Current season: actual goals / observed home or away games, from SEASON_BASE_STATS."
        : "Aktuelle Saison: tatsächliche Tore / beobachtete Heim- bzw. Auswärtsspiele, aus SEASON_BASE_STATS.",
      expected: en
        ? "Current season: stored pre-match forecasts, aggregated per venue in USER_SEASON_STATS. Diagnostic; not an independent calibration target."
        : "Aktuelle Saison: gespeicherte Pre-Match-Prognosen je Spielort aus USER_SEASON_STATS. Diagnose; kein eigenständiger Kalibrierungs-Zielwert.",
      should: en
        ? "Current season: stored Should goals / covered games in USER_SEASON_STATS; absent data stays missing."
        : "Aktuelle Saison: gespeicherte Should Goals / abgedeckte Spiele in USER_SEASON_STATS; fehlende Daten bleiben fehlend.",
      market: en
        ? "Current season: stored market-derived goal expectations. The calibration uses matched Market/Expected when available, not necessarily this raw value."
        : "Aktuelle Saison: gespeicherte Markt-Torerwartungen. Die Kalibrierung nutzt, soweit vorhanden, passend abgeglichene Market/Expected-Werte statt dieses Rohwerts.",
      previous: en
        ? "Previous season: actual goals per venue, SEASON_BASE_STATS."
        : "Vorsaison: tatsächliche Tore je Spielort, SEASON_BASE_STATS.",
      previous_saved: en
        ? "Stored previous-season settings, identity checked. Sample sizes come from previous-season actual games; not a certified season-end snapshot."
        : "Gespeicherte Vorsaison-Settings mit Identitätsprüfung. Fallzahlen aus realen Vorsaisonspielen; kein zertifizierter Saisonabschluss-Snapshot.",
      previous_should: en
        ? "Previous season: stored Should goals only for covered games, USER_SEASON_STATS. This is not current-season Should."
        : "Vorsaison: gespeicherte Should Goals nur für abgedeckte Spiele, USER_SEASON_STATS. Keine aktuellen Should-Werte.",
    }[key]);
  const arrow = (before, after) =>
    after > before + 0.0005 ? "↑" : after < before - 0.0005 ? "↓" : "=";
  const comparison = (before, after) =>
    Math.abs(after - before) < 0.0005
      ? `${fmt(after)} =`
      : `${fmt(before)} → ${fmt(after)} (${percent(after / before - 1)})${
          Math.abs(after / before - 1) >= 0.2 ? " !" : ""
        }`;
  const team = context?.teams.find((t) => t.team_id === detail),
    row = preview?.rows.find((r) => r.team_id === detail);
  const overview = team && row ? teamOverview(team, row) : null;
  return (
    <div className="league-evolution league-analysis">
      <p>{t("fullIntro")}</p>
      {batchAdmin && !singleLeague && (
        <LeagueAnalysisBatch
          catalog={catalog}
          profile={profile}
          market={market}
          language={i18n.language}
          run={batch}
          onChange={setBatch}
          onBusy={setBatchBusy}
          disabled={!!phase || dirty}
          onOpen={(item, batchId) => {
            reset();
            setSelected(String(item.league_id));
            setContext({
              ...item.context,
              revision: item.saved?.revision || item.context.revision,
            });
            setSaved(item.status !== "ready");
            setResult(item.draft);
            setTargets(item.draft.team_targets);
            setPreview(item.draft.edit_preview || item.draft.preview);
            setBatchOrigin(batchId);
            setRunId(`${batchId}-${item.season_id}`);
            setRevisions([]);
          }}
        />
      )}

      <details>
        <summary>{t("help")}</summary>
        <p>{t("fullHelp")}</p>
        <p>{t("profileHelp")}</p>
        <p>
          {i18n.language?.startsWith("en")
            ? "Deterministic settings calibration · no OpenAI calls"
            : "Deterministische Settings-Kalibrierung · keine OpenAI-Aufrufe"}
        </p>
        {result?.calibration && (
          <details>
            <summary>
              {i18n.language?.startsWith("en")
                ? "Profile parameters"
                : "Profilparameter"}
            </summary>
            <pre>{JSON.stringify(result.calibration, null, 2)}</pre>
          </details>
        )}
        <p>{t("marketHelp")}</p>
      </details>
      <LeagueChoice
        leagues={availableLeagues}
        value={selected}
        disabled={busy}
        label={t("league")}
        placeholder={t("choose")}
        onChange={(value) => {
          if (!dirty || window.confirm(t("discardConfirm"))) load(value);
        }}
      />
      {context && (
        <>
          <h3>
            {context.league.name} · {context.season.name}
          </h3>
          <label>
            {t("profile")}: {profile}
            <input
              aria-label={t("profile")}
              type="range"
              min="0"
              max="100"
              value={profile}
              disabled={busy}
              onChange={(e) => {
                setProfile(Number(e.target.value));
                reset();
              }}
            />
          </label>
          <div className="la-profile">
            <span>{t("conservative")}</span>
            <span>{t("moderate")}</span>
            <span>{t("reactive")}</span>
          </div>
          <label>
            {t("marketOrientation")}{" "}
            <input
              type="range"
              min="0"
              max="2"
              step="1"
              aria-label={t("marketOrientation")}
              aria-valuetext={t(`market_${market}`)}
              value={["low", "normal", "strong"].indexOf(market)}
              disabled={busy}
              onChange={(e) => {
                setMarket(["low", "normal", "strong"][Number(e.target.value)]);
                reset();
              }}
            />
            <span className="la-profile">
              {["low", "normal", "strong"].map((k) => (
                <span key={k}>{t(`market_${k}`)}</span>
              ))}
            </span>
          </label>
          <button
            disabled={busy}
            onClick={() =>
              act("analyze", async () => {
                reset();
                const fresh = await request("context");
                setContext(fresh.context);
                const r = await request("analyze", {
                  revision: fresh.context.revision,
                });
                setResult(r);
                setTargets(r.team_targets);
                setPreview(r.edit_preview || r.preview);
              })
            }
          >
            {t("analyzeFull")}
          </button>
          <details>
            <summary>{t("evidence")}</summary>
            <div className="le-scroll">
              <table>
                <thead>
                  <tr>
                    <th>{t("source")}</th>
                    <th>{t("games")}</th>
                    {["stored_goal_average", "exp_home_factor", "drawRate"].map(
                      (k) => (
                        <th key={k}>
                          {k === "previous_saved"
                            ? i18n.language?.startsWith("en")
                              ? "Previous saved"
                              : "Vorsaison gespeichert"
                            : t(k)}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>{t("currentModel")}</th>
                    <td>—</td>
                    <td>{fmt(context.current_model?.stored_goal_average)}</td>
                    <td>{fmt(context.current_model?.exp_home_factor)}</td>
                    <td>—</td>
                  </tr>
                  {context.league_metrics?.map((r) => (
                    <tr key={r.key}>
                      <th>
                        {t(r.key === "expected" ? "historicalExpected" : r.key)}
                      </th>
                      <td>{r.fixtures}</td>
                      <td>{fmt(r.goalAverage)}</td>
                      <td>{fmt(r.homeAdvantage)}</td>
                      <td>{percent(r.drawRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p>{t("modelHelp")}</p>
            <p>{t("missingData")}</p>
          </details>
          {result && preview && (
            <section>
              <h3>
                {context.league.name} – {t("fullDraft")}
              </h3>
              <p>
                {t("analyzedCount", { count: context.teams.length })} ·{" "}
                {t("changedDetails", {
                  count: preview.summary.changed_details,
                })}
              </p>
              {result.calibration && (
                <p aria-label="Calibration parameters">
                  Should Weight: {fmt(result.calibration.should_weight)} ·
                  Current Game Multiplier:{" "}
                  {fmt(result.calibration.current_game_multiplier)} ·
                  Performance Multiplier:{" "}
                  {fmt(result.calibration.performance_multiplier)} · Market
                  Weight: {percent(result.calibration.market_weight)}
                </p>
              )}
              <p>
                {i18n.language?.startsWith("en")
                  ? "Unnormalized target values. ! marks changes of at least 20%; review the calculation details."
                  : "Zielwerte vor Normalisierung. ! markiert Änderungen ab 20 %; bitte im Rechenweg prüfen."}
              </p>
              <p>
                <b>
                  {i18n.language.startsWith("en")
                    ? "Model parameters · Saved / Draft"
                    : "Modellparameter · Gespeichert / Entwurf"}
                </b>
                {previewDirty &&
                  " · " +
                    (i18n.language.startsWith("en")
                      ? "Save preview pending"
                      : "Save-Vorschau ausstehend")}
              </p>
              <p className="la-stage-note">
                {en
                  ? "Draft parameters are derived from the four proposed team values before normalization. The save preview shows the final normalized team values and season parameters. Draw factor is retained."
                  : "Entwurfsparameter entstehen aus den vier vorgeschlagenen Teamwerten vor Normalisierung. Die Save-Vorschau zeigt die final normalisierten Teamwerte und Saisonparameter. Der Remisfaktor bleibt erhalten."}
              </p>
              <div className="la-metrics">
                {leagueFields.map((field) => (
                  <div className="la-metric" key={field}>
                    <small>{t(field)}</small>
                    <strong>
                      {comparison(
                        context.parameters[field],
                        preview.parameters[field]
                      )}{" "}
                      {Math.abs(
                        preview.parameters[field] - context.parameters[field]
                      ) >= 0.0005 && (
                        <span>
                          {arrow(
                            context.parameters[field],
                            preview.parameters[field]
                          )}
                        </span>
                      )}
                    </strong>
                  </div>
                ))}
              </div>
              <details>
                <summary>{t("reasoning")}</summary>
                <p>
                  {result.suggestions.find((s) => s.id === "league")?.reasoning}
                </p>
              </details>
              <CalibrationReview
                key={context.season.id}
                context={context}
                preview={preview}
                normalized={result.preview}
                targets={targets}
                automaticTargets={result.team_targets}
                pending={previewDirty}
                busy={busy}
                en={i18n.language.startsWith("en")}
                onDetail={(id) => {
                  setDetail(id);
                  setEditing(false);
                }}
              />
              {team && row && (
                <Dialog
                  open
                  onClose={() => {
                    setDetail(null);
                    setEditing(false);
                  }}
                  maxWidth="xl"
                  fullWidth
                  aria-label={`${team.name} ${t("details")}`}
                  PaperProps={{
                    sx: {
                      bgcolor: "#152936",
                      color: "#edf3f7",
                      maxHeight: "90dvh",
                    },
                  }}
                >
                  <DialogContent className="league-analysis la-detail">
                    <h3 className="la-team-name">
                      {team.logo && <img src={team.logo} alt="" />}
                      {team.name}
                    </h3>
                    <details>
                      <summary>
                        {i18n.language.startsWith("en")
                          ? "Last six matches / context"
                          : "Letzte sechs Spiele / Kontext"}
                      </summary>
                      <TeamContext
                        team={team}
                        seasonId={context.season.id}
                        initialLimit={6}
                      />
                    </details>
                    <button
                      disabled={busy || saved}
                      onClick={() => {
                        setEditing(!editing);
                      }}
                    >
                      {t(editing ? "endEdit" : "edit")}
                    </button>
                    <button
                      onClick={() => {
                        setDetail(null);
                        setEditing(false);
                      }}
                    >
                      {t("close")}
                    </button>
                    <details className="la-source-help">
                      <summary>
                        {en
                          ? "Sources, periods and samples"
                          : "Quellen, Zeiträume und Fallzahlen"}
                      </summary>
                      <p>
                        {en ? "Current season" : "Aktuelle Saison"}:{" "}
                        {context.season.name} ·{" "}
                        {en ? "Previous season" : "Vorsaison"}:{" "}
                        {context.previous_season?.name ?? "—"}.{" "}
                        {en
                          ? "n is the number of games for this source and venue. Zero weight for missing data."
                          : "n ist die Anzahl Spiele dieser Quelle und dieses Spielorts. Fehlende Daten erhalten kein Gewicht."}
                      </p>
                      <ul>
                        {[
                          "actual",
                          "expected",
                          "should",
                          "market",
                          "previous",
                          "previous_saved",
                          "previous_should",
                        ].map((k) => (
                          <li key={k}>
                            <b>
                              {k === "previous_saved"
                                ? en
                                  ? "Previous saved"
                                  : "Vorsaison gespeichert"
                                : t(k)}
                              :
                            </b>{" "}
                            {sourceHint(k)}{" "}
                            {!team.references[k] && (
                              <strong>
                                {en
                                  ? " Missing / not usable."
                                  : " Fehlend / nicht verwendbar."}
                              </strong>
                            )}
                          </li>
                        ))}
                      </ul>
                    </details>
                    <div className="le-scroll">
                      <table
                        className="la-reference-table"
                        aria-label={
                          en
                            ? "Base settings and references"
                            : "Basiswerte und Referenzen"
                        }
                      >
                        <thead>
                          <tr>
                            {[
                              "field",
                              "original",
                              "draft",
                              "actual",
                              "expected",
                              "should",
                              "market",
                              "previous",
                              "previous_saved",
                              "previous_should",
                            ].map((k) => (
                              <th key={k} title={sourceHint(k)}>
                                {k === "previous_saved"
                                  ? i18n.language?.startsWith("en")
                                    ? "Previous saved"
                                    : "Vorsaison gespeichert"
                                  : t(k)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {detailFields.map((field) => (
                            <tr key={field}>
                              <th>{t(field)}</th>
                              <td className="la-primary-value">
                                {fmt(team.setting[field])}
                              </td>
                              <td className="la-primary-value">
                                {fmt(row[field])}
                                {editing && (
                                  <input
                                    aria-label={`${team.name} ${t(field)}`}
                                    type="number"
                                    step="0.001"
                                    min="0.001"
                                    disabled={busy || saved}
                                    defaultValue={Number(
                                      targets[team.team_id][field].toFixed(3)
                                    )}
                                    onChange={(e) => {
                                      const value = Number(e.target.value);
                                      const next = {
                                        ...targets,
                                        [team.team_id]: {
                                          ...targets[team.team_id],
                                          [field]: value,
                                        },
                                      };
                                      setTargets(next);
                                      try {
                                        const rows = previewDetailDraft(
                                          context.teams.map((t) => t.setting),
                                          next,
                                          result.drawfactor,
                                          { normalize: false }
                                        );
                                        setPreview((prev) => ({
                                          ...prev,
                                          rows,
                                          summary: detailSummary(
                                            context.teams,
                                            rows
                                          ),
                                        }));
                                        setError("");
                                      } catch (_) {
                                        setError(
                                          i18n.language.startsWith("en")
                                            ? "Enter positive draft values."
                                            : "Bitte positive Entwurfswerte eingeben."
                                        );
                                      }
                                      setPreviewDirty(true);
                                    }}
                                  />
                                )}
                              </td>
                              {[
                                "actual",
                                "expected",
                                "should",
                                "market",
                                "previous",
                                "previous_saved",
                                "previous_should",
                              ].map((k) => (
                                <td key={k} title={sourceHint(k)}>
                                  {fmt(
                                    team.references[k]?.fixtureSettingValues?.[
                                      field
                                    ]
                                  )}
                                  <small>
                                    n ={" "}
                                    {Number.isFinite(
                                      team.references[k]?.[
                                        field.endsWith("_home")
                                          ? "home"
                                          : "away"
                                      ]?.played
                                    )
                                      ? team.references[k][
                                          field.endsWith("_home")
                                            ? "home"
                                            : "away"
                                        ].played.toLocaleString(i18n.language)
                                      : "—"}
                                  </small>
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p>
                      {[
                        "for_total",
                        "against_total",
                        "total_strength",
                        "total_goals",
                        "for_homefactor",
                        "against_homefactor",
                      ]
                        .map((k) => `${t(k)}: ${fmt(row[k])}`)
                        .join(" · ")}
                    </p>
                    {overview && (
                      <section
                        aria-label={
                          i18n.language.startsWith("en")
                            ? "Team overview"
                            : "Team-Gesamtbild"
                        }
                      >
                        <p>
                          {en
                            ? "Setting-scale comparison: home/away balanced 50/50. These source-derived values are not the observed whole-season average. Strength = mean For / mean Against; Goal Sum = mean For + mean Against. Home factors = home rate / venue-balanced mean."
                            : "Vergleich auf Setting-Skala: Heim/Auswärts 50/50-neutralisiert. Diese Quellen-Ableitungen sind kein beobachteter Gesamtsaisonschnitt. Strength = Mittel For / Mittel Against; Goal Sum = Mittel For + Mittel Against. Heimfaktoren = Heimrate / neutralisiertes Mittel."}
                        </p>
                        <p>
                          {en
                            ? "Actual season, weighted by played games"
                            : "Tatsächliche Saison, nach Spielen gewichtet"}
                          : n = {team.references.actual?.played ?? "—"} ·{" "}
                          {en ? "Goal ratio" : "Torquotient"}:{" "}
                          {fmt(team.references.actual?.quotient)} ·{" "}
                          {en ? "Goals per game" : "Torschnitt"}:{" "}
                          {fmt(team.references.actual?.goalAverage)}
                        </p>
                        {overviewMetrics.map((metric) => (
                          <p key={metric}>
                            <b>{t(metric)}:</b>{" "}
                            {Object.entries(overview.sources)
                              .map(
                                ([source, values]) =>
                                  `${
                                    source === "saved"
                                      ? t("original")
                                      : source === "previous_saved"
                                      ? i18n.language.startsWith("en")
                                        ? "Previous saved"
                                        : "Vorsaison gespeichert"
                                      : t(source)
                                  } ${fmt(values[metric])}`
                              )
                              .join(" · ")}
                          </p>
                        ))}
                        {team.previous_saved_status === "record_absent" && (
                          <p>
                            {en
                              ? "Previous saved: no historical team record in this league/season. No substitute value used."
                              : "Vorsaison gespeichert: kein historischer Teamrecord dieser Liga/Saison. Kein Ersatzwert verwendet."}
                          </p>
                        )}
                        {team.previous_saved_status?.startsWith(
                          "record_rejected"
                        ) && (
                          <p>
                            {i18n.language.startsWith("en")
                              ? "Previous saved: record exists but its identity check failed; not used for calibration."
                              : "Vorsaison gespeichert: Record vorhanden, aber Identitätsprüfung fehlgeschlagen; nicht in der Kalibrierung verwendet."}
                          </p>
                        )}
                        {overview.diagnostics.map((d) => (
                          <p key={d.metric} role="status">
                            {t(d.metric)}:{" "}
                            {i18n.language.startsWith("en")
                              ? "Review overall direction: at least two current sources differ materially; the draft responds weakly or in the opposite direction. Diagnostic only."
                              : "Gesamtrichtung prüfen: Mindestens zwei aktuelle Quellen weichen deutlich ab; der Entwurf reagiert schwach oder gegenläufig. Nur Diagnose."}
                          </p>
                        ))}
                      </section>
                    )}
                    {batchAdmin && result.calibration && (
                      <details>
                        <summary>
                          {i18n.language?.startsWith("en")
                            ? "Calculation breakdown"
                            : "Rechenweg"}
                        </summary>
                        <p>
                          {en
                            ? "Automatic calculation before manual edits; final working values are shown in the save preview. Each source is weighted by its venue sample size. No interpolation back to saved settings."
                            : "Automatische Berechnung vor manuellen Korrekturen; finale Arbeitswerte stehen in der Save-Vorschau. Jede Quelle wird mit ihrer Spielort-Fallzahl gewichtet. Keine zusätzliche Rückmischung auf gespeicherte Settings."}
                        </p>
                        {result.suggestions
                          .filter(
                            (s) => s.id === String(team.team_id) && s.breakdown
                          )
                          .map((s) => {
                            const b = s.breakdown;
                            return (
                              <div className="la-calculation" key={s.field}>
                                <b>{t(s.field)}</b>
                                <p>
                                  {en
                                    ? "Historical blend / weight"
                                    : "Vorsaison-Mischwert / Gewicht"}
                                  : {fmt(b.previous_base)} /{" "}
                                  {fmt(b.historical_weight)} · Real{" "}
                                  {percent(b.previous_weights?.real)}, Should{" "}
                                  {percent(b.previous_weights?.should)},{" "}
                                  {en ? "saved" : "gespeichert"}{" "}
                                  {percent(b.previous_weights?.saved)}
                                </p>
                                <p>
                                  {en
                                    ? "Current blend / weight"
                                    : "Aktueller Mischwert / Gewicht"}
                                  : {fmt(b.current_performance)} /{" "}
                                  {fmt(b.current_weight)} · Real{" "}
                                  {percent(b.current_weights?.real)}, Should{" "}
                                  {percent(b.current_weights?.should)}
                                </p>
                                <p>
                                  {en
                                    ? "Recent games / weight"
                                    : "Letzte Spiele / Gewicht"}
                                  : {fmt(b.performance_value)} /{" "}
                                  {fmt(b.performance_weight)} (n ={" "}
                                  {b.performance_games}) ·{" "}
                                  {en
                                    ? "Saved prior weight"
                                    : "Gewicht gespeicherter Prior"}
                                  : {fmt(b.stability_prior_weight)}
                                </p>
                                <p>
                                  {en
                                    ? "Evidence target → Market target (effective share) → Proposal"
                                    : "Datenziel → Marktziel (effektiver Anteil) → Vorschlag"}
                                  : {fmt(b.evidence_target)} →{" "}
                                  {fmt(b.market_value)} (
                                  {percent(b.market_weight)}) →{" "}
                                  <b>{fmt(b.proposed)}</b>
                                </p>
                                <p>
                                  {b.market_interpretation ===
                                  "saved * matched_market / matched_expected"
                                    ? en
                                      ? "Market target = saved × matched Market / matched Expected."
                                      : "Marktziel = gespeichert × abgeglichener Market / abgeglichener Expected."
                                    : en
                                    ? "Raw market target; no matched Expected available."
                                    : "Markt-Rohwert als Ziel; kein passender Expected-Abgleich vorhanden."}
                                </p>
                              </div>
                            );
                          })}
                        <details>
                          <summary>
                            {en
                              ? "Full calculation data"
                              : "Vollständige Rechendaten"}
                          </summary>
                          <pre
                            style={{
                              whiteSpace: "pre-wrap",
                              fontSize: "0.9rem",
                            }}
                          >
                            {JSON.stringify(
                              {
                                parameters: result.calibration,
                                overview,
                                fields: result.suggestions
                                  .filter((s) => s.id === String(team.team_id))
                                  .map((s) => ({
                                    field: s.field,
                                    ...s.breakdown,
                                  })),
                              },
                              null,
                              2
                            )}
                          </pre>
                        </details>
                      </details>
                    )}
                    {result.suggestions
                      .filter((s) => s.id === String(team.team_id))
                      .map((s) => (
                        <p className="la-reason" key={s.field}>
                          <b>{t(s.field)}:</b> {s.reasoning}
                        </p>
                      ))}
                  </DialogContent>
                </Dialog>
              )}
              <p>{t("normalizationNote")}</p>
              <button
                disabled={
                  busy ||
                  saved ||
                  previewDirty ||
                  !context.atomic_save_available
                }
                onClick={() =>
                  act("save", async () => {
                    const savedResult = await request(
                      batchOrigin ? "batch-save" : "save-draft",
                      {
                        ...(batchOrigin ? { batch_id: batchOrigin } : {}),
                        team_targets: targets,
                        drawfactor: result.drawfactor,
                        confirm: true,
                        run_id: runId,
                      }
                    );
                    setContext((c) => ({
                      ...c,
                      revision: savedResult.revision,
                    }));
                    setSaved(true);
                    onSaved?.(savedResult);
                    if (batchOrigin) {
                      const snapshot = savedResult.snapshot
                        ? await request("batch-snapshot-check", {
                            batch_id: batchOrigin,
                            restore_id: savedResult.snapshot.id,
                          })
                        : null;
                      setBatch((b) =>
                        b
                          ? {
                              ...b,
                              items: b.items.map((i) =>
                                i.season_id === context.season.id
                                  ? {
                                      ...i,
                                      status:
                                        savedResult.saved === false
                                          ? "unchanged"
                                          : "saved",
                                      saved: savedResult,
                                      snapshot,
                                      draft: {
                                        ...i.draft,
                                        team_targets: targets,
                                        edit_preview: preview,
                                        preview: result.preview,
                                        drawfactor: result.drawfactor,
                                      },
                                      summary: summarizeDraft(context, {
                                        preview,
                                      }),
                                    }
                                  : i
                              ),
                            }
                          : b
                      );
                    }
                    const rev = await request("revisions");
                    setRevisions(rev.revisions);
                  })
                }
              >
                {t("saveFull")}
              </button>
              {!context.atomic_save_available && (
                <p role="alert">{t("atomicUnavailable")}</p>
              )}
            </section>
          )}
          {result && previewDirty && (
            <button
              disabled={busy || invalidTargets}
              onClick={() =>
                act("preview", async () => {
                  const r = await request("detail-preview", {
                    team_targets: targets,
                    drawfactor: result.drawfactor,
                  });
                  setPreview(r.edit_preview || r.preview);
                  setResult((old) => ({
                    ...old,
                    preview: r.preview,
                    edit_preview: r.edit_preview,
                  }));
                  setPreviewDirty(false);
                  setEditing(false);
                })
              }
            >
              {t("updateDraft")}
            </button>
          )}
          {batchOrigin && result && (
            <button
              disabled={busy || previewDirty}
              onClick={() => {
                if (!saved)
                  setBatch((b) =>
                    b
                      ? {
                          ...b,
                          items: b.items.map((i) =>
                            i.season_id === context.season.id
                              ? {
                                  ...i,
                                  draft: {
                                    ...i.draft,
                                    team_targets: targets,
                                    drawfactor: result.drawfactor,
                                    edit_preview: preview,
                                    preview: result.preview,
                                  },
                                  summary: summarizeDraft(context, { preview }),
                                }
                              : i
                          ),
                        }
                      : b
                  );
                reset();
              }}
            >
              {i18n.language.startsWith("en")
                ? "Back to batch overview"
                : "Zur Gesamtübersicht"}
            </button>
          )}
          {result && !saved && (
            <button
              disabled={busy}
              onClick={() => {
                if (batchOrigin)
                  setBatch((b) =>
                    b
                      ? {
                          ...b,
                          items: b.items.map((i) =>
                            i.season_id === context.season.id
                              ? {
                                  ...i,
                                  status: "skipped",
                                  error: "DISCARDED_BY_ADMIN",
                                }
                              : i
                          ),
                        }
                      : b
                  );
                reset();
              }}
            >
              {t("discard")}
            </button>
          )}
          {saved && (
            <>
              <p role="status">{t("saved")}</p>
              <p>{t("compileSeparate")}</p>
              {onUpcoming && (
                <button
                  onClick={() =>
                    onUpcoming({
                      league_id: context.league.id,
                      season_id: context.season.id,
                    })
                  }
                >
                  {t("openUpcoming")}
                </button>
              )}
            </>
          )}
          {!!revisions.length && (
            <details>
              <summary>{t("revisions")}</summary>
              {revisions.map((rev) => (
                <p key={rev.id}>
                  {new Date(rev.at).toLocaleString(i18n.language)}{" "}
                  <button
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm(t("restoreConfirm")))
                        act("restore", async () => {
                          await request("restore", {
                            restore_id: rev.id,
                            run_id: newRunId(),
                            confirm: true,
                          });
                          await load(selected);
                        });
                    }}
                  >
                    {t("restore")}
                  </button>
                </p>
              ))}
            </details>
          )}
        </>
      )}
      {busy && <p role="status">{t("working")}</p>}
      {error && (
        <p role="alert">
          {tr(`help.leagueSettingsAnalysis.errors.${error}`, {
            defaultValue: t("unknownError"),
          })}
        </p>
      )}
    </div>
  );
}
